/**
 * Persistent Audio + AJAX Navigation
 *
 * Upload this as an Asset in your theme (e.g. assets/persistent-nav.js)
 * and load it in theme.liquid with:
 *   <script src="{{ 'persistent-nav.js' | asset_url }}" defer></script>
 *
 * IMPORTANT: This intercepts internal link clicks and swaps page content
 * via fetch(), instead of doing a full browser reload. That's what lets
 * the <audio> element (which lives outside the swapped region) keep playing
 * uninterrupted. Test thoroughly — cart drawers, analytics, and other
 * scripts that expect a fresh page load may need adjusting.
 *
 * Note: the requestIdleCallback polyfill for Safari lives inline in
 * theme.liquid's <head> (not here), since it needs to load before
 * predictive-search.js runs — much earlier than this file loads.
 */

(function () {
  const CONTENT_SELECTOR = '#MainContent'; // the region that gets swapped
  const audio = document.getElementById('site-bg-audio');
  const iconMuted = document.getElementById('icon-muted');
  const iconPlaying = document.getElementById('icon-playing');

  function updateIcon() {
    if (!audio) return;
    const playing = !audio.paused;
    if (iconMuted) iconMuted.style.display = playing ? 'none' : 'block';
    if (iconPlaying) iconPlaying.style.display = playing ? 'block' : 'none';
  }

  // Note: clicking the button itself is handled by window.toggleBgAudio,
  // defined inline in theme.liquid's <head> and wired up via the button's
  // onclick attribute — not here. That's deliberately more robust than an
  // addEventListener attached from this deferred external script.

  // On first load, try to resume if the user had it playing before.
  // Browsers will likely block this unless they have strong "media
  // engagement" history with your site — expect this to only work
  // for some returning visitors, not universally.
  window.addEventListener('DOMContentLoaded', function () {
    if (localStorage.getItem('bgAudioPlaying') === '1' && audio) {
      audio.play().then(updateIcon).catch(function () {
        localStorage.setItem('bgAudioPlaying', '0');
        updateIcon();
      });
    } else {
      updateIcon();
    }
  });

  // ---- AJAX-style navigation ----
  // Intercepts clicks on same-origin links, fetches the target page,
  // swaps out #MainContent, and updates the URL — without a full reload.
  // This is what actually keeps the <audio> element alive across "page views".

  async function navigateTo(url, addToHistory = true) {
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Fetch failed');
      const html = await res.text();
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, 'text/html');

      const newContent = newDoc.querySelector(CONTENT_SELECTOR);
      const currentContent = document.querySelector(CONTENT_SELECTOR);
      if (!newContent || !currentContent) {
        // Fallback: couldn't find the region, do a real navigation
        window.location.href = url;
        return;
      }

      currentContent.replaceWith(newContent);
      document.title = newDoc.title;

      if (addToHistory) {
        history.pushState({}, '', url);
      }

      window.scrollTo(0, 0);

      // Different pages can rely on different CSS/JS that's only loaded
      // into <head> for templates that actually use it (e.g. a product
      // slideshow's styles won't be on the homepage, which has no
      // slideshows). Since we never touch <head> otherwise, components
      // "new" to this page would render completely unstyled. Copy over
      // anything present in the new page's <head> that isn't already
      // loaded in the live document.
      syncHeadAssets(newDoc.head);

      // Videos with the `autoplay` attribute (like the homepage hero video)
      // don't reliably autoplay when created via DOMParser and inserted
      // afterward — browsers mostly reserve automatic autoplay for elements
      // parsed natively as the page loads. Explicitly kick off playback for
      // any such videos in the newly inserted content.
      newContent.querySelectorAll('video[autoplay]').forEach(function (video) {
        video.muted = true; // required for autoplay without user interaction
        video.play().catch(function (err) {
          console.error('Hero video autoplay failed:', err);
        });
      });

      // Some elements (the header bar and the logo overlay) live OUTSIDE
      // #MainContent and are added/removed entirely by Liquid depending on
      // the template (e.g. the header-group only renders when
      // template != 'index'; logo-overlay only renders when template ==
      // 'index'). Since Liquid doesn't re-run on an AJAX swap, we sync
      // these elements manually: pull them from the freshly fetched HTML
      // and insert/replace/remove them to match what the real page would have.
      syncOuterElement('header-group', newDoc, newContent, true);
      syncOuterElement('logo-overlay', newDoc, document.getElementById('persistent-audio-player'), false);

      // logo-overlay fades itself in via an inline <script> that runs on
      // DOMContentLoaded — which only fires once, on a real page load.
      // Scripts inserted via DOM manipulation (like our sync above) don't
      // auto-execute, so the logo would otherwise stay stuck invisible
      // (opacity: 0). Set it directly instead.
      const logoOverlay = document.getElementById('logo-overlay');
      if (logoOverlay) {
        logoOverlay.style.opacity = '1';
      }

      // Re-run Shopify's section/theme init scripts for the new content.
      // Many themes fire a custom event or re-init via Shopify's Theme
      // Editor hooks — check your theme's JS for what needs re-triggering
      // (e.g. slideshow init, variant picker, etc.) after a swap like this.
      document.dispatchEvent(new CustomEvent('page:swapped'));

      // Some components (like the header) recalculate their own height/
      // layout on window resize. Firing a resize event after the swap
      // nudges those components to re-run their setup logic.
      window.dispatchEvent(new Event('resize'));

      // The header's height variables and menu style (desktop text menu vs.
      // mobile hamburger drawer) are calculated by theme.liquid ONCE on a
      // real page load. Since we just inserted a fresh header-group, those
      // calculations need to run again for it. These are exposed globally
      // in theme.liquid specifically so we can call them here.
      if (typeof window.setHeaderHeighCustomProperties === 'function') {
        window.setHeaderHeighCustomProperties();
      }
      if (typeof window.setHeaderMenuStyle === 'function') {
        window.setHeaderMenuStyle();
      }

      // Some browsers natively pause background media during back/forward
      // navigation (pressing the actual browser back button), even though
      // we intercept the navigation ourselves. If it was supposed to still
      // be playing, resume it immediately — this still counts as part of
      // the same user gesture (the back button click), so it's allowed.
      if (audio && localStorage.getItem('bgAudioPlaying') === '1' && audio.paused) {
        audio.play().catch(function (err) {
          console.error('Failed to resume audio after navigation:', err);
        });
      }

    } catch (err) {
      console.error('AJAX navigation failed, falling back to full load', err);
      window.location.href = url;
    }
  }

  /**
   * Syncs an element (by ID) that lives outside #MainContent between the
   * live document and the freshly fetched page.
   * - If the new page has it: replace the current one (or insert it before
   *   referenceNode if it doesn't currently exist).
   * - If the new page doesn't have it: remove the current one, if present.
   *
   * If skipIfBothExist is true and the element exists on BOTH the current
   * and new page, we leave the current one completely untouched instead of
   * replacing it. This matters for header-group specifically: it contains
   * stateful components (like the cart drawer) that only initialize
   * correctly on a real page load. Replacing an already-working header with
   * an identical-looking but freshly-parsed one on every single navigation
   * would silently break those components for no benefit, since the header
   * is the same on every non-homepage page anyway.
   */
  function syncOuterElement(id, newDoc, referenceNode, skipIfBothExist) {
    const newEl = newDoc.getElementById(id);
    const currentEl = document.getElementById(id);

    if (newEl) {
      if (currentEl) {
        if (skipIfBothExist) return; // leave the existing, already-working one alone
        currentEl.replaceWith(newEl);
      } else if (referenceNode && referenceNode.parentNode) {
        referenceNode.parentNode.insertBefore(newEl, referenceNode);
      } else {
        document.body.appendChild(newEl);
      }
    } else if (currentEl) {
      currentEl.remove();
    }
  }

  /**
   * Copies over stylesheets, inline styles, and scripts from the freshly
   * fetched page's <head> that aren't already present in the live document.
   * This is what lets components "new" to a page (not present on whatever
   * page the visitor started on) get their required CSS/JS after an AJAX swap.
   */
  function syncHeadAssets(newHead) {
    // External stylesheets
    newHead.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
      const href = link.getAttribute('href');
      if (!href) return;
      const alreadyLoaded = document.head.querySelector(
        'link[rel="stylesheet"][href="' + CSS.escape(href) + '"]'
      );
      if (!alreadyLoaded) {
        const clone = document.createElement('link');
        clone.rel = 'stylesheet';
        clone.href = href;
        document.head.appendChild(clone);
      }
    });

    // Inline <style> blocks (Shopify often injects section-specific critical
    // CSS this way). Dedupe by exact content match.
    const existingStyleContents = Array.from(document.head.querySelectorAll('style')).map(
      function (s) { return s.textContent; }
    );
    newHead.querySelectorAll('style').forEach(function (style) {
      if (existingStyleContents.indexOf(style.textContent) === -1) {
        const clone = document.createElement('style');
        clone.textContent = style.textContent;
        document.head.appendChild(clone);
      }
    });

    // External scripts (e.g. custom element definitions a new component needs).
    // Note: re-running these can be risky if a script doesn't guard against
    // redefining an already-registered custom element — test carefully.
    newHead.querySelectorAll('script[src]').forEach(function (script) {
      const src = script.getAttribute('src');
      if (!src) return;
      const alreadyLoaded =
        document.head.querySelector('script[src="' + CSS.escape(src) + '"]') ||
        document.body.querySelector('script[src="' + CSS.escape(src) + '"]');
      if (!alreadyLoaded) {
        const clone = document.createElement('script');
        Array.from(script.attributes).forEach(function (attr) {
          clone.setAttribute(attr.name, attr.value);
        });
        document.head.appendChild(clone);
      }
    });
  }

  // Fallback for the cart drawer: Shopify loads its click behavior via a
  // low-priority script (cart-drawer.js). Browser privacy protections
  // (Safari's "Advanced Tracking Protection", Brave Shields, etc.) can
  // occasionally block this outright, even though it's not actually a
  // tracker — leaving the button visually present but permanently inert
  // until a reload. Unlike visual/animation-based checks (which proved
  // unreliable), this checks a simple, concrete fact: did the script
  // actually load and register its custom element at all? If not after a
  // generous wait, fall back to a real navigation to /cart, which always
  // works since it's a plain page load with no JS dependency.
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-testid="cart-drawer-trigger"]');
    if (!trigger) return;

    setTimeout(function () {
      const componentLoaded = !!customElements.get('cart-drawer-component');
      if (!componentLoaded) {
        window.location.href = '/cart';
      }
    }, 3000);
  });

  document.addEventListener('click', function (e) {
    const link = e.target.closest('a');
    if (!link) return;

    const url = link.getAttribute('href');
    if (!url || url.startsWith('#') || link.target === '_blank') return;

    // Only intercept same-origin links
    const isSameOrigin = link.origin === window.location.origin;
    if (!isSameOrigin) return;

    // Skip cart/checkout/account links — let Shopify handle those normally
    if (/\/(cart|checkout|account)/.test(url)) return;

    e.preventDefault();

    // Resume audio SYNCHRONOUSLY here, immediately on click, before any
    // async work. Some browsers (Safari especially) only allow .play()
    // without restriction if it's called strictly within the original
    // user gesture — waiting on an `await fetch(...)` first, even briefly,
    // can cause the browser to no longer consider it "part of the click,"
    // silently blocking playback. Attempting it here, before navigateTo's
    // async work begins, keeps it safely inside that window.
    if (audio && localStorage.getItem('bgAudioPlaying') === '1' && audio.paused) {
      audio.play().catch(function (err) {
        console.error('Failed to resume audio on click:', err);
      });
    }

    navigateTo(url);
  });

  window.addEventListener('popstate', function () {
    // Same reasoning as above: attempt this synchronously, right as the
    // popstate event fires, before navigateTo's async work begins.
    if (audio && localStorage.getItem('bgAudioPlaying') === '1' && audio.paused) {
      audio.play().catch(function (err) {
        console.error('Failed to resume audio on popstate:', err);
      });
    }
    navigateTo(window.location.href, false);
  });
})();
